import { Grid, Typography } from '@material-ui/core'
import { bind, Subscribe } from '@react-rxjs/core'
import React from 'react'
import { Observable, of, switchMap } from 'rxjs'
import { map } from 'rxjs/operators'
import styled from 'styled-components'

import { ProductLabelStatus } from '../../../graphql/search'
import {
  watchCartItemsLabelStatus$,
  WatchLabelStatusBySku,
} from '../../../lib/cart/state/cart-items/cart-item-remove'
import { reduceIterable } from '../../../utils/collectionTools'
import LabelStatusIcon from '../../LabelStatusIcon'
import { StyledFunctionComponent } from '../../types'

type MapValue<A> = A extends Map<unknown, infer B> ? B : never

const [useSomeProductInCartNeedsLabel] = bind(
  watchCartItemsLabelStatus$.pipe(
    switchMap((labelStatusBySku$) => {
      const reduceLabelStatus = reduceIterable(
        of(false),
        (acc$: Observable<boolean>, labelStatus$: MapValue<WatchLabelStatusBySku>) =>
          // sequentially check each label status until one is found with `ABSENT` status
          acc$.pipe(
            switchMap((acc) =>
              acc
                ? // short-circuit if product already found with ABSENT label status
                  of(acc)
                : // return whether label status is ABSENT
                  labelStatus$.pipe(
                    map(
                      (result) =>
                        // ignore errors - just check label status
                        result._tag === 'Right' &&
                        result.data.labelStatus === ProductLabelStatus.Absent,
                    ),
                  ),
            ),
          ),
      )
      return reduceLabelStatus(labelStatusBySku$.values())
    }),
  ),
)

const StyledLabelStatusIcon = styled(LabelStatusIcon)`
  margin-right: 5px;
`

const ProductRequiringLabelMessageReactive: StyledFunctionComponent = ({ className }) => {
  const someProductInCartNeedsLabel = useSomeProductInCartNeedsLabel()
  return !someProductInCartNeedsLabel ? null : (
    <Grid container className={className}>
      <Grid container item wrap="nowrap" alignItems="center">
        <Grid item>
          <StyledLabelStatusIcon isLabelPresent={false} />
        </Grid>
        <Grid item>
          <Typography display="block" variant="caption">
            <strong>One or more products do not have a label design on file.</strong>
          </Typography>
        </Grid>
      </Grid>
      <Grid item>
        <Typography display="block" variant="caption">
          You may purchase these products now, and we will ship them after completing the template
          creation and approval process.
        </Typography>
      </Grid>
    </Grid>
  )
}

const ProductsRequiringLabelMessage: StyledFunctionComponent = ({ className }) => (
  <Subscribe fallback={null}>
    <ProductRequiringLabelMessageReactive className={className} />
  </Subscribe>
)

const StyledProductsRequiringLabelMessage = styled(ProductsRequiringLabelMessage)`
  && {
    flex-basis: 100%;
    line-height: 14px;
    margin: 8px 15px 8px 15px;
    text-align: left;
  }
`

export default StyledProductsRequiringLabelMessage
